import { NetworkPort, RawHttpRequest, RawHttpResponse } from '../ports';

export class FetchNetworkAdapter implements NetworkPort {
  private baseURL: string;

  constructor(baseURL?: string) {
    if (baseURL) {
      this.baseURL = baseURL;
    } else {
      this.baseURL =
        import.meta.env.VITE_API_BASE_URL ||
        (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
          ? `${window.location.protocol}//${window.location.hostname
              .replace('almaysarah-dashboard', 'almaysarah-backend')
              .replace('dashboard', 'backend')}/api`
          : 'http://localhost:3000/api');
    }
  }

  async send(request: RawHttpRequest): Promise<RawHttpResponse> {
    const fullUrl = request.url.startsWith('http')
      ? request.url
      : `${this.baseURL}${request.url.startsWith('/') ? '' : '/'}${request.url}`;

    // Combine timeout and caller abort signal
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (request.timeoutMs && request.timeoutMs > 0) {
      timeoutId = setTimeout(() => {
        controller.abort(new Error(`Request timeout after ${request.timeoutMs}ms`));
      }, request.timeoutMs);
    }

    if (request.signal) {
      if (request.signal.aborted) {
        controller.abort(request.signal.reason);
      } else {
        request.signal.addEventListener('abort', () => {
          controller.abort(request.signal?.reason);
        });
      }
    }

    try {
      let body: BodyInit | undefined;
      const headers = { ...request.headers };

      if (request.body !== undefined && request.body !== null) {
        if (
          typeof FormData !== 'undefined' &&
          request.body instanceof FormData
        ) {
          body = request.body;
          // Let fetch set Content-Type with multipart boundary
          delete headers['Content-Type'];
          delete headers['content-type'];
        } else if (
          typeof Blob !== 'undefined' &&
          request.body instanceof Blob
        ) {
          body = request.body;
        } else if (typeof request.body === 'string') {
          body = request.body;
        } else {
          body = JSON.stringify(request.body);
          if (!headers['Content-Type'] && !headers['content-type']) {
            headers['Content-Type'] = 'application/json';
          }
        }
      }

      const response = await fetch(fullUrl, {
        method: request.method,
        headers,
        body,
        signal: controller.signal,
      });

      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((val, key) => {
        responseHeaders[key.toLowerCase()] = val;
      });

      let parsedBody: unknown;
      const responseType = request.responseType || 'json';

      if (responseType === 'blob') {
        parsedBody = await response.blob();
      } else if (responseType === 'stream') {
        parsedBody = response.body;
      } else if (responseType === 'text') {
        parsedBody = await response.text();
      } else if (responseType === 'arraybuffer') {
        parsedBody = await response.arrayBuffer();
      } else {
        const text = await response.text();
        if (text) {
          try {
            parsedBody = JSON.parse(text);
          } catch {
            parsedBody = text;
          }
        } else {
          parsedBody = null;
        }
      }

      return {
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        body: parsedBody,
      };
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }
}
